import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import StudentLayout from '@/components/layouts/StudentLayout';
import ProLayout from '@/components/layouts/ProLayout';
import { 
  Video, Calendar, Clock, Users, User, BookOpen,
  ArrowLeft, Plus, Play, Loader2, CheckCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const LiveTutoringPage = () => {
  const navigate = useNavigate();
  const { user, api } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [availableSessions, setAvailableSessions] = useState([]);
  const [mySessions, setMySessions] = useState([]);
  const [activeTab, setActiveTab] = useState('available');
  const [showCreateModal, setShowCreateModal] = useState(false);

  const isTutor = user?.role === 'tutor' || user?.role === 'admin';

  useEffect(() => {
    fetchSessions();
  }, []);

  const fetchSessions = async () => {
    try {
      const [availableRes, myRes] = await Promise.all([
        api.get('/tutoring/sessions/available'),
        api.get('/tutoring/sessions/my-sessions')
      ]);
      setAvailableSessions(availableRes.data.sessions);
      setMySessions(myRes.data.sessions);
    } catch (error) {
      console.error('Error fetching sessions:', error);
      toast.error('Failed to load sessions');
    } finally {
      setLoading(false);
    }
  };

  const bookSession = async (sessionId) => {
    try {
      await api.post(`/tutoring/sessions/${sessionId}/book`);
      toast.success('Session booked successfully!');
      fetchSessions();
    } catch (error) {
      console.error('Error booking session:', error);
      toast.error(error.response?.data?.detail || 'Failed to book session');
    }
  };

  const joinSession = (sessionId) => {
    navigate(`/tutoring/room/${sessionId}`);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const SessionCard = ({ session, isMySession = false }) => {
    const isBooked = session.booked_students?.some(s => 
      s.student_id === user?.id || s.booked_by === user?.id
    );
    const canJoin = isMySession || isBooked || session.tutor_id === user?.id;
    const isFull = session.session_type === 'one_to_one' 
      ? session.booked_students?.length >= 1 
      : session.booked_students?.length >= session.max_participants;

    return (
      <div className="bg-white rounded-2xl border-2 border-slate-200 p-5 hover:shadow-lg transition-all">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
              session.session_type === 'group' ? 'bg-purple-100' : 'bg-emerald-100'
            }`}>
              {session.session_type === 'group' ? (
                <Users className="w-6 h-6 text-purple-600" />
              ) : (
                <User className="w-6 h-6 text-emerald-600" />
              )}
            </div>
            <div>
              <h3 className="font-bold text-slate-800">{session.title}</h3>
              <p className="text-sm text-slate-500">
                with {session.tutor_name}
              </p>
            </div>
          </div>
          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
            session.status === 'scheduled' ? 'bg-blue-100 text-blue-700' :
            session.status === 'in_progress' ? 'bg-green-100 text-green-700' :
            'bg-slate-100 text-slate-700'
          }`}>
            {session.status === 'in_progress' ? 'LIVE' : session.status}
          </span>
        </div>

        <div className="space-y-2 mb-4">
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <Calendar className="w-4 h-4" />
            <span>{formatDate(session.scheduled_start)}</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <Clock className="w-4 h-4" />
            <span>{session.duration_minutes} minutes</span>
          </div>
          {session.subject && (
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <BookOpen className="w-4 h-4" />
              <span className="capitalize">{session.subject}</span>
            </div>
          )}
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <Users className="w-4 h-4" />
            <span>
              {session.booked_students?.length || 0} / {session.session_type === 'one_to_one' ? 1 : session.max_participants} booked
            </span>
          </div>
        </div>

        {session.description && (
          <p className="text-sm text-slate-500 mb-4">{session.description}</p>
        )}

        <div className="flex gap-2">
          {canJoin && session.status !== 'completed' ? (
            <Button 
              onClick={() => joinSession(session.id)}
              className="flex-1 bg-emerald-600 hover:bg-emerald-700"
            >
              <Play className="w-4 h-4 mr-2" />
              {session.status === 'in_progress' ? 'Join Now' : 'Join Session'}
            </Button>
          ) : isBooked ? (
            <Button variant="outline" className="flex-1" disabled>
              <CheckCircle className="w-4 h-4 mr-2" />
              Booked
            </Button>
          ) : isFull ? (
            <Button variant="outline" className="flex-1" disabled>
              Session Full
            </Button>
          ) : (
            <Button 
              onClick={() => bookSession(session.id)}
              className="flex-1"
            >
              Book This Session
            </Button>
          )}
        </div>
      </div>
    );
  };

  const CreateSessionModal = () => {
    const [formData, setFormData] = useState({
      title: '',
      session_type: 'one_to_one',
      scheduled_start: '',
      duration_minutes: 60,
      max_participants: 10,
      subject: '',
      description: ''
    });
    const [creating, setCreating] = useState(false);

    const handleSubmit = async (e) => {
      e.preventDefault();
      setCreating(true);
      
      try {
        await api.post('/tutoring/sessions/create', formData);
        toast.success('Session created successfully!');
        setShowCreateModal(false);
        fetchSessions();
      } catch (error) {
        console.error('Error creating session:', error);
        toast.error('Failed to create session');
      } finally {
        setCreating(false);
      }
    };

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
          <h2 className="font-bold text-xl mb-4">Create Tutoring Session</h2>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Session Title
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                required
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                placeholder="e.g., GCSE Maths Revision"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Session Type
              </label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    value="one_to_one"
                    checked={formData.session_type === 'one_to_one'}
                    onChange={(e) => setFormData({...formData, session_type: e.target.value})}
                  />
                  <span>1:1 Session</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    value="group"
                    checked={formData.session_type === 'group'}
                    onChange={(e) => setFormData({...formData, session_type: e.target.value})}
                  />
                  <span>Group Session</span>
                </label>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Scheduled Start
              </label>
              <input
                type="datetime-local"
                value={formData.scheduled_start}
                onChange={(e) => setFormData({...formData, scheduled_start: e.target.value})}
                required
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:outline-none"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Duration (minutes)
                </label>
                <select
                  value={formData.duration_minutes}
                  onChange={(e) => setFormData({...formData, duration_minutes: parseInt(e.target.value)})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                >
                  <option value={30}>30 min</option>
                  <option value={45}>45 min</option>
                  <option value={60}>60 min</option>
                  <option value={90}>90 min</option>
                  <option value={120}>2 hours</option>
                </select>
              </div>

              {formData.session_type === 'group' && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Max Participants
                  </label>
                  <input
                    type="number"
                    min={2}
                    max={50}
                    value={formData.max_participants}
                    onChange={(e) => setFormData({...formData, max_participants: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                  />
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Subject (optional)
              </label>
              <select
                value={formData.subject}
                onChange={(e) => setFormData({...formData, subject: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:outline-none"
              >
                <option value="">Select subject</option>
                <option value="maths">Maths</option>
                <option value="english">English</option>
                <option value="science">Science</option>
                <option value="esol">ESOL</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Description (optional)
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                rows={3}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                placeholder="Brief description of what will be covered..."
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowCreateModal(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={creating}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700"
              >
                {creating ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Plus className="w-4 h-4 mr-2" />
                )}
                Create Session
              </Button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  const content = (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-800">Live Tutoring</h1>
          <p className="text-slate-600">Join live sessions with tutors</p>
        </div>
        
        {isTutor && (
          <Button 
            onClick={() => setShowCreateModal(true)}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Session
          </Button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-4 mb-6">
        <button
          onClick={() => setActiveTab('available')}
          className={`px-4 py-2 rounded-lg font-semibold transition-all ${
            activeTab === 'available'
              ? 'bg-emerald-600 text-white'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
        >
          Available Sessions
        </button>
        <button
          onClick={() => setActiveTab('my')}
          className={`px-4 py-2 rounded-lg font-semibold transition-all ${
            activeTab === 'my'
              ? 'bg-emerald-600 text-white'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
        >
          My Sessions
        </button>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {activeTab === 'available' ? (
            availableSessions.length > 0 ? (
              availableSessions.map(session => (
                <SessionCard key={session.id} session={session} />
              ))
            ) : (
              <div className="col-span-full text-center py-12 bg-slate-50 rounded-2xl">
                <Video className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                <p className="text-slate-500">No available sessions at the moment</p>
                <p className="text-sm text-slate-400">Check back later for new sessions</p>
              </div>
            )
          ) : (
            mySessions.length > 0 ? (
              mySessions.map(session => (
                <SessionCard key={session.id} session={session} isMySession />
              ))
            ) : (
              <div className="col-span-full text-center py-12 bg-slate-50 rounded-2xl">
                <Calendar className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                <p className="text-slate-500">No sessions booked yet</p>
                <p className="text-sm text-slate-400">Browse available sessions to get started</p>
              </div>
            )
          )}
        </div>
      )}

      {showCreateModal && <CreateSessionModal />}
    </div>
  );

  // Use appropriate layout based on user role
  if (user?.role === 'student') {
    return <StudentLayout>{content}</StudentLayout>;
  }

  const sidebarItems = [
    { path: user?.role === 'tutor' ? '/tutor' : '/admin', label: 'Dashboard', icon: Video },
  ];

  return (
    <ProLayout sidebarItems={sidebarItems} title="Live Tutoring">
      {content}
    </ProLayout>
  );
};

export default LiveTutoringPage;
